import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  EventAnalyticsEntry,
  EventAnalyticsFilters,
  EventAnalyticsMetrics,
  EventAnalyticsPagination,
  DateRangePeriod,
} from "../types/analytics";
import eventsAnalyticsApi, {
  EventAnalytics,
} from "../services/api/events-analytics.api";
import { logger } from "../utils/logger";
import {
  type AnalyticsScope,
  getScopedEvents,
  getScopedEventMetrics,
} from "../services/api/analytics-router";
import { type ScopedArg, extractArg } from "./participantsAnalyticsSlice";

// ─── Normalizers ──────────────────────────────────────────────────────────────

export const normalizeEventAnalyticsEntry = (
  entry: EventAnalytics,
): EventAnalyticsEntry => ({
  id: entry.id,
  title: entry.title,
  type: entry.type,
  // AD-360: статус берётся как есть. Прежняя подстановка «запланировано»
  // превращала молчание сервера в утверждение о мероприятии; сервер же
  // отдаёт здесь каноничный статус сущности, а слова `planned` не знает вовсе.
  status: entry.status,
  startDate: entry.startDate,
  endDate: entry.endDate,
  participantsCount: entry.participantsCount ?? 0,
  registrationsCount: entry.registrationsCount ?? 0,
  conversionRate: entry.conversionRate ?? 0,
  revenue: entry.revenue ?? 0,
  averageRating: entry.averageRating ?? 0,
  completionRate: entry.completionRate ?? 0,
  organizerId: entry.organizerId,
  organizerName: entry.organizerName,
  category: entry.category,
  location: entry.location,
  capacity: entry.capacity ?? 0,
  attendanceRate: entry.attendanceRate ?? 0,
  engagementScore: entry.engagementScore ?? 0,
  totalInteractions: entry.totalInteractions ?? 0,
  socialShares: entry.socialShares ?? 0,
  feedbackCount: entry.feedbackCount ?? 0,
  averageSessionDuration: entry.averageSessionDuration ?? 0,
  peakAttendance: entry.peakAttendance ?? 0,
  revenuePerParticipant: entry.revenuePerParticipant ?? 0,
  costPerAcquisition: entry.costPerAcquisition ?? 0,
  returnOnInvestment: entry.returnOnInvestment ?? 0,
});

export const normalizeEventAnalyticsMetrics = (
  metrics: Partial<EventAnalyticsMetrics>,
): EventAnalyticsMetrics => ({
  overview: {
    totalEvents: Number(metrics?.overview?.totalEvents ?? 0),
    activeEvents: Number(metrics?.overview?.activeEvents ?? 0),
    completedEvents: Number(metrics?.overview?.completedEvents ?? 0),
    cancelledEvents: Number(metrics?.overview?.cancelledEvents ?? 0),
    upcomingEvents: Number(metrics?.overview?.upcomingEvents ?? 0),
  },
  participation: {
    totalParticipants: Number(metrics?.participation?.totalParticipants ?? 0),
    totalRegistrations: Number(metrics?.participation?.totalRegistrations ?? 0),
    averageConversionRate: Number(
      metrics?.participation?.averageConversionRate ?? 0,
    ),
    averageAttendanceRate: Number(
      metrics?.participation?.averageAttendanceRate ?? 0,
    ),
    peakAttendance: Number(metrics?.participation?.peakAttendance ?? 0),
  },
  financial: {
    totalRevenue: Number(metrics?.financial?.totalRevenue ?? 0),
    averageRevenuePerEvent: Number(
      metrics?.financial?.averageRevenuePerEvent ?? 0,
    ),
    averageRevenuePerParticipant: Number(
      metrics?.financial?.averageRevenuePerParticipant ?? 0,
    ),
    costPerAcquisition: Number(metrics?.financial?.costPerAcquisition ?? 0),
    returnOnInvestment: Number(metrics?.financial?.returnOnInvestment ?? 0),
  },
  engagement: {
    averageRating: Number(metrics?.engagement?.averageRating ?? 0),
    averageEngagementScore: Number(
      metrics?.engagement?.averageEngagementScore ?? 0,
    ),
    totalInteractions: Number(metrics?.engagement?.totalInteractions ?? 0),
    socialShares: Number(metrics?.engagement?.socialShares ?? 0),
    feedbackCount: Number(metrics?.engagement?.feedbackCount ?? 0),
  },
  performance: {
    averageCompletionRate: Number(
      metrics?.performance?.averageCompletionRate ?? 0,
    ),
    averageSessionDuration: Number(
      metrics?.performance?.averageSessionDuration ?? 0,
    ),
    mostPopularCategory: String(
      metrics?.performance?.mostPopularCategory ?? "",
    ),
    mostActiveTime: String(metrics?.performance?.mostActiveTime ?? ""),
    mostActiveDay: String(metrics?.performance?.mostActiveDay ?? ""),
  },
  trends: {
    eventsByMonth: (metrics?.trends?.eventsByMonth ?? []).map((item) => ({
      month: String(item?.month ?? ""),
      count: Number(item?.count ?? 0),
      revenue: Number(item?.revenue ?? 0),
    })),
    participantsByEvent: (metrics?.trends?.participantsByEvent ?? []).map(
      (item) => ({
        eventId: String(item?.eventId ?? ""),
        eventTitle: String(item?.eventTitle ?? ""),
        participants: Number(item?.participants ?? 0),
      }),
    ),
    revenueByEvent: (metrics?.trends?.revenueByEvent ?? []).map((item) => ({
      eventId: String(item?.eventId ?? ""),
      eventTitle: String(item?.eventTitle ?? ""),
      revenue: Number(item?.revenue ?? 0),
    })),
    popularCategories: (metrics?.trends?.popularCategories ?? []).map(
      (item) => ({
        category: String(item?.category ?? ""),
        count: Number(item?.count ?? 0),
        revenue: Number(item?.revenue ?? 0),
      }),
    ),
  },
});

export const normalizeEventAnalyticsFilters = (
  filters: EventAnalyticsFilters = {},
): EventAnalyticsFilters => {
  const result: EventAnalyticsFilters = {
    dateRange: filters.dateRange ?? DateRangePeriod.LAST_30,
    page: filters.page ?? 1,
    limit: filters.limit ?? 10,
  };
  if (filters.eventId !== undefined) result.eventId = filters.eventId;
  if (filters.eventType !== undefined) result.eventType = filters.eventType;
  if (filters.status !== undefined) result.status = filters.status;
  if (filters.category !== undefined) result.category = filters.category;
  if (filters.organizerId !== undefined)
    result.organizerId = filters.organizerId;
  if (filters.startDate !== undefined) result.startDate = filters.startDate;
  if (filters.endDate !== undefined) result.endDate = filters.endDate;
  return result;
};

export const normalizeEventAnalyticsPagination = (
  pagination: Partial<EventAnalyticsPagination> | null | undefined,
): EventAnalyticsPagination | null => {
  if (!pagination) return null;
  return {
    page: Number(pagination?.page ?? 1),
    limit: Number(pagination?.limit ?? 10),
    total: Number(pagination?.total ?? 0),
    totalPages: Number(pagination?.totalPages ?? 0),
  };
};

// ─── API payload shape ────────────────────────────────────────────────────────

interface EventsApiShape {
  events?: EventAnalytics[];
  pagination?: Partial<EventAnalyticsPagination>;
  filters?: EventAnalyticsFilters;
}
interface EventsApiPayload extends EventsApiShape {
  data?: EventsApiShape;
}

// ─── State ────────────────────────────────────────────────────────────────────

export interface EventsAnalyticsState {
  items: EventAnalyticsEntry[];
  metrics: EventAnalyticsMetrics | null;
  filters: EventAnalyticsFilters;
  pagination: EventAnalyticsPagination | null;
  loading: {
    list: boolean;
    metrics: boolean;
  };
  error: string | null;
}

const initialState: EventsAnalyticsState = {
  items: [],
  metrics: null,
  filters: {},
  pagination: null,
  loading: { list: false, metrics: false },
  error: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchEventAnalytics = createAsyncThunk(
  "events/fetchEventAnalytics",
  async (arg: ScopedArg<EventAnalyticsFilters> = {}) => {
    try {
      const { filters, scope } = extractArg(
        arg as
          | EventAnalyticsFilters
          | { filters: EventAnalyticsFilters; scope: AnalyticsScope },
        {},
      );
      if (scope !== "platform") {
        const result = await getScopedEvents(scope, filters);
        return {
          events: result.events,
          pagination: normalizeEventAnalyticsPagination(result.pagination),
          filters: normalizeEventAnalyticsFilters(result.filters),
        };
      }
      const response = await eventsAnalyticsApi.getEvents(filters);
      const payload = response.data as EventsApiPayload;
      const result = payload?.data ?? payload;
      const rawEvents: EventAnalytics[] = result?.events ?? [];
      const pagination = result?.pagination ?? payload?.pagination ?? null;
      const responseFilters = result?.filters ?? payload?.filters ?? filters;
      return {
        events: rawEvents.map(normalizeEventAnalyticsEntry),
        pagination: normalizeEventAnalyticsPagination(pagination),
        filters: normalizeEventAnalyticsFilters(responseFilters),
      };
    } catch (error) {
      logger.error("Не удалось загрузить аналитику событий", error);
      throw error;
    }
  },
);

export const fetchEventMetrics = createAsyncThunk(
  "events/fetchEventMetrics",
  async (arg: ScopedArg<EventAnalyticsFilters> = {}) => {
    try {
      const { filters, scope } = extractArg(
        arg as
          | EventAnalyticsFilters
          | { filters: EventAnalyticsFilters; scope: AnalyticsScope },
        {},
      );
      if (scope !== "platform") {
        return await getScopedEventMetrics(scope, filters);
      }
      const response = await eventsAnalyticsApi.getEventsMetrics(filters);
      return normalizeEventAnalyticsMetrics(
        response.data as Partial<EventAnalyticsMetrics>,
      );
    } catch (error) {
      logger.error("Не удалось получить метрики событий", error);
      throw error;
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const eventsAnalyticsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchEventAnalytics.pending, (state) => {
        state.loading.list = true;
        state.error = null;
      })
      .addCase(fetchEventAnalytics.fulfilled, (state, action) => {
        state.loading.list = false;
        state.items = action.payload.events;
        state.pagination = action.payload.pagination;
        state.filters = action.payload.filters;
      })
      .addCase(fetchEventAnalytics.rejected, (state, action) => {
        state.loading.list = false;
        state.error =
          action.error.message ?? "Ошибка загрузки аналитики событий";
      })

      .addCase(fetchEventMetrics.pending, (state) => {
        state.loading.metrics = true;
      })
      .addCase(fetchEventMetrics.fulfilled, (state, action) => {
        state.loading.metrics = false;
        state.metrics = action.payload;
      })
      .addCase(fetchEventMetrics.rejected, (state, action) => {
        state.loading.metrics = false;
        state.error = action.error.message ?? "Ошибка загрузки метрик событий";
      }),
});

export default eventsAnalyticsSlice.reducer;
